// W36: thin wrapper over the generated webhooks client.
//
// Outbound webhook subscriptions + delivery audit (`services/webhooks`). Types
// come from `./generated/webhooks` (regenerate with
// `pnpm --filter @repletoryreach/sdk-api generate webhooks`); the request path
// stays on the house core so the envelope, cursor pagination, Idempotency-Key,
// token refresh and ApiError mapping behave exactly as before.
//
// Every path here is the one the service actually routes, under
// `/v1/webhooks/subscriptions`: the module previously advertised a flat
// `/v1/webhooks/{id}` surface that 404'd.

import { http } from './_core/request.js';
import { isApiError } from './_core/errors.js';
import type { Envelope } from './_core/envelope.js';
import type { Page, PageOpts } from './_core/pagination.js';
import { buildPageQuery } from './_core/pagination.js';
import type * as gen from './generated/webhooks/index.js';

// ── Subscriptions (generated) ──────────────────────────────────────────────

/**
 * A subscription as list/get/patch return it: `secret_prefix` (the first 14
 * chars of the signing secret: enough to identify it, useless to replay it)
 * and never the secret itself. `failure_count` is the consecutive-failure
 * streak, reset to 0 by the next success.
 */
export type WebhookSubscription = gen.WebhookSubscriptionDto;

/**
 * `POST /v1/webhooks/subscriptions` only. Deliberately a distinct type from
 * {@link WebhookSubscription}: create is the one response that carries the
 * plaintext signing `secret`, and it is returned exactly once.
 */
export type CreatedWebhook = gen.WebhookSubscriptionCreatedDto;

export type CreateWebhookRequest = gen.CreateSubscriptionRequest;
export type UpdateWebhookRequest = gen.PatchSubscriptionRequest;

// ── Deliveries (generated) ─────────────────────────────────────────────────

/** Terminal + in-flight delivery states. `blocked` is the W45 SSRF refusal. */
export type WebhookDeliveryResult =
  | 'pending' | 'success' | 'failed' | 'dead' | 'blacklisted' | 'blocked';

/**
 * One delivery attempt.
 *
 * ⚠ DRIFT; the service has **two** delivery projections. The per-subscription
 * list (`GET /v1/webhooks/subscriptions/{id}/deliveries`) and the single
 * delivery returned by a replay both include `created_at`; the org-wide
 * `GET /v1/webhooks/deliveries` omits it, so on rows from
 * {@link listAllDeliveries} `created_at` is absent despite being declared
 * required here. It is left required because that is the surface the consoles
 * have compiled against since the module shipped, {@link WebhookDeliveryListItem}
 * is the exact org-wide shape.
 *
 * `result` is pinned to the six states the service emits; the OpenAPI document
 * types it as an open `string`.
 */
export type WebhookDelivery = Omit<gen.WebhookDeliveryDto, 'result'> & {
  result: WebhookDeliveryResult;
} & DeliveryResponseBody;

/** The exact `GET /v1/webhooks/deliveries` row shape: no `created_at`. */
export type WebhookDeliveryListItem = Omit<gen.WebhookDeliveryListItem, 'result'> & {
  result: WebhookDeliveryResult;
} & DeliveryResponseBody;

/**
 * What the target answered with, trimmed to its first 500 characters.
 *
 * The status code says a delivery failed; this says why, and it is usually the
 * receiver's own error message. Null when nothing was sent (a pending or
 * blocked attempt) or the target answered with an empty body.
 *
 * Hand-typed: the service projects it on every delivery read path, and
 * `src/generated/webhooks` is one regeneration behind
 * (`pnpm --filter @repletoryreach/sdk-api generate webhooks`).
 */
export interface DeliveryResponseBody {
  response_body_excerpt?: string | null;
}

export type ReplayResult = gen.ReplayResultDto;

// ── Errors ─────────────────────────────────────────────────────────────────

/**
 * Stable error codes the webhooks service returns.
 *
 * `INVALID_TARGET` (W45): HTTP 400 on create/update when the target URL is not a
 * publicly routable http(s) endpoint: a non-http(s) scheme, credentials embedded in
 * the URL, an internal-only name (`.local`, `.internal`, `localhost`), or a host that
 * resolves into private space: RFC1918, loopback, link-local (including the cloud
 * metadata address 169.254.169.254), CGNAT, multicast or reserved ranges. The response
 * carries `error.details.reason` with the specific reason code.
 */
export type WebhookErrorCode = 'INVALID_TARGET' | 'NOT_FOUND';

/** @see WebhookErrorCode */
export const WEBHOOK_INVALID_TARGET = 'INVALID_TARGET';

/** True when `err` is the 400 `INVALID_TARGET` rejection from create/update. */
export function isInvalidTarget(err: unknown): boolean {
  if (!isApiError(err) || err.status !== 400) return false;
  const body = err.body as { error?: { code?: string } } | null;
  return err.code === WEBHOOK_INVALID_TARGET || body?.error?.code === WEBHOOK_INVALID_TARGET;
}

/** The specific reason the target was refused, e.g. `link_local_address`. */
export function invalidTargetReason(err: unknown): string | null {
  if (!isInvalidTarget(err) || !isApiError(err)) return null;
  const body = err.body as gen.InvalidTargetEnvelope | null;
  return body?.error?.details?.reason ?? null;
}

// ── subscriptions ───────────────────────────────────────────────────────────

export function listWebhooks(): Promise<Envelope<WebhookSubscription[]>> {
  return http.get<Envelope<WebhookSubscription[]>>('/v1/webhooks/subscriptions');
}

export function getWebhook(id: string): Promise<Envelope<WebhookSubscription>> {
  return http.get<Envelope<WebhookSubscription>>(`/v1/webhooks/subscriptions/${encodeURIComponent(id)}`);
}

export function createWebhook(req: CreateWebhookRequest): Promise<Envelope<CreatedWebhook>> {
  return http.post<Envelope<CreatedWebhook>>('/v1/webhooks/subscriptions', { body: req });
}

export function updateWebhook(id: string, req: UpdateWebhookRequest): Promise<Envelope<WebhookSubscription>> {
  return http.patch<Envelope<WebhookSubscription>>(
    `/v1/webhooks/subscriptions/${encodeURIComponent(id)}`, { body: req }
  );
}

export function deleteWebhook(id: string): Promise<void> {
  return http.delete<void>(`/v1/webhooks/subscriptions/${encodeURIComponent(id)}`);
}

// ── deliveries ──────────────────────────────────────────────────────────────

/** Query for {@link listDeliveries}. `cursor` wins when both are given. */
export interface DeliveryListOpts extends PageOpts {
  /** Plain row offset: the same convention `contacts.listContacts` uses. */
  offset?: number;
  /**
   * Keep only attempts that ended this way. `failed` is widened server-side to
   * every terminal unhappy state (`failed`, `dead`, `blacklisted`, `blocked`),
   * because "did it arrive?" does not distinguish between a retry that ran out,
   * a 4xx that stopped it and a target the safety check refused.
   *
   * The parameter was sent by both consoles and bound by neither the SDK nor
   * the service, so a "failures only" toggle narrowed nothing.
   */
  result?: WebhookDeliveryResult;
  /** Keep only attempts for one event type, e.g. `message.failed`. */
  event_type?: string;
}

/**
 * Attempts for ONE subscription, newest first. W46: `limit`, `cursor` and
 * `offset` are bound and honoured and `meta` carries `total` + `next_cursor`,
 * so `paginate()` walks every attempt; before W46 only `limit` reached the
 * handler and everything past the first page was unreachable.
 */
export function listDeliveries(webhookId: string, opts?: DeliveryListOpts): Promise<Page<WebhookDelivery>> {
  const query: Record<string, string | number | undefined> = { ...buildPageQuery(opts) };
  if (opts?.offset !== undefined) query.offset = opts.offset;
  if (opts?.result !== undefined) query.result = opts.result;
  if (opts?.event_type !== undefined) query.event_type = opts.event_type;
  return http.get<Page<WebhookDelivery>>(
    `/v1/webhooks/subscriptions/${encodeURIComponent(webhookId)}/deliveries`,
    { query }
  );
}

/**
 * Every delivery across every subscription in the organisation.
 *
 * ⚠ DRIFT; these rows are the {@link WebhookDeliveryListItem} projection: they
 * carry no `created_at`, even though the declared `WebhookDelivery` requires it.
 * Order by `scheduled_at` instead.
 */
export function listAllDeliveries(subscriptionId?: string): Promise<Page<WebhookDelivery>> {
  return http.get<Page<WebhookDelivery>>('/v1/webhooks/deliveries', {
    query: subscriptionId ? { subscription_id: subscriptionId } : {}
  });
}

/** Requeue one attempt. The target is re-checked by the SSRF guard before it fires. */
export function replayDelivery(webhookId: string, deliveryId: string): Promise<Envelope<WebhookDelivery>> {
  return http.post<Envelope<WebhookDelivery>>(
    `/v1/webhooks/subscriptions/${encodeURIComponent(webhookId)}/deliveries/${encodeURIComponent(deliveryId)}/replay`
  );
}

/** Requeue every failed/dead delivery for a subscription from the last 24 h. */
export function replaySubscription(webhookId: string): Promise<Envelope<ReplayResult>> {
  return http.post<Envelope<ReplayResult>>(
    `/v1/webhooks/subscriptions/${encodeURIComponent(webhookId)}/replay`
  );
}

/**
 * Sends a test delivery to a subscription.
 *
 * Queued through the normal delivery path, so it exercises the same signing,
 * SSRF guard and retry policy as a real event and shows up in the delivery log
 * beside them. A test send is the standard affordance on a webhooks screen and
 * there was no endpoint for it, so the only way to find out whether a URL
 * worked was to wait for a real event.
 */
export function testSubscription(id: string): Promise<Envelope<gen.WebhookTestResultDto>> {
  return http.post<Envelope<gen.WebhookTestResultDto>>(
    `/v1/webhooks/subscriptions/${encodeURIComponent(id)}/test`
  );
}
